import { Illustration, Text } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Stack, ThemeProvider } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { populateSessionAfterLogin } from "@src/app/appInit/populateSessionAfterLogin";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent } from "react";
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { LoadingButton } from "../../components/LoadingButton";
import { PageFooterWrapper } from "../../components/PageFooterWrapper";
import { PLACEMENTS_BASE_PATH } from "../../Placements/paths";
import { SHIFT_DISCOVERY_PATH } from "../../ShiftDiscovery/paths";
import { useCompleteSignup } from "../api/useCompleteSignup";
import { useShouldShowJobsFirst } from "../hooks/useShouldShowJobsFirst";
import { ONBOARDING_STAGES } from "../util/constants";
import { logOnboardingError, logOnboardingEvent } from "../util/segment";

export function WelcomePage() {
  const worker = useDefinedWorker();

  const dispatch = useDispatch();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const history = useHistory();

  const { mutateAsync: completeSignup, isLoading } = useCompleteSignup();
  const shouldShowJobsFirst = useShouldShowJobsFirst();

  useLogEffect(APP_V2_USER_EVENTS.WELCOME_PAGE_VIEWED);

  const onNext = async (event?: FormEvent | MouseEvent) => {
    event?.preventDefault();
    try {
      logOnboardingEvent(APP_V2_USER_EVENTS.ENTERED_APP, {
        workerId: worker.userId,
        email: worker.email,
      });
      await completeSignup();

      await queryClient.refetchQueries({ queryKey: [GET_WORKER_QUERY_KEY] });

      // Remove this once redux is no longer used
      /**
       * We need to call this again because the `userId` is not set in signup until this
       * point. When the user enter the OTP to sign-up, we are setting the `isAuthorized`
       * to true. When that happens, we don't set the Redux session state called `userId`,
       * which is needed in many places later (such as displaying open shifts). The below
       * call is needed to set the required properties, such as `userId`, in the Redux
       * session state.
       *
       * The ideal solution would be to either not rely on Redux, or to populate Redux
       * session's `userId` using the API response. But since that refactor is large, we
       * are doing it this way for now.
       *
       * There might also be a doubt about why populate at this onboarding step, and not
       * before (say) right after user enters the email and name. The reason is that this
       * step of onboarding is the last step of the signup process, and is guaranteed to
       * have the information, while the previous steps might be prone to flakiness.
       */
      await populateSessionAfterLogin(dispatch, { isSignup: true });

      if (shouldShowJobsFirst) {
        history.push(`${RootPaths.APP_V2_HOME}/${PLACEMENTS_BASE_PATH}`);
      } else {
        history.push(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_PATH}`);
      }
    } catch (error) {
      if (error instanceof Error) {
        logOnboardingError(ONBOARDING_STAGES.FINAL, error, { workerId: worker.userId });
      }

      showErrorToast("Something went wrong. Please try again later.");
    }
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => ({
          background: theme.palette.onboarding?.neutral.gradient,
        })}
      >
        <PageContent>
          <Stack flex={1} alignItems="center" justifyContent="center" gap={7}>
            <Illustration type="clipboard-bunny" />
            <Text variant="h2" sx={{ textAlign: "center" }}>
              You&apos;re all set! 🎉
            </Text>
            <Text variant="h5" sx={{ textAlign: "center" }}>
              You&apos;re ready to start browsing work opportunities on Clipboard!
            </Text>
          </Stack>
        </PageContent>
        <PageFooterWrapper width="100%" padding={5} alignItems="center">
          <LoadingButton fullWidth size="large" isLoading={isLoading} onClick={onNext}>
            Start browsing for work
          </LoadingButton>
        </PageFooterWrapper>
      </PageWrapper>
    </ThemeProvider>
  );
}
